<script setup>
import { useSelfOrderStore } from '@/stores/selfOrder'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const route = useRoute()
const router = useRouter()
const store = useSelfOrderStore()

const isLoading = ref(true)
const errorMessage = ref('')

const startSession = async () => {
  isLoading.value = true
  errorMessage.value = ''

  try {
    const session = await store.scanTable(route.params.qrToken)

    // Belum ada customer teridentifikasi (session baru / belum isi nama-HP) -> ke halaman identify.
    // Kalau session lama (scan ulang QR meja yang sama) sudah punya customer, langsung ke menu.
    if (session.customer?.id)
      router.replace({ name: 'order-session-token-menu', params: { sessionToken: session.session_token } })
    else
      router.replace({ name: 'order-session-token-identify', params: { sessionToken: session.session_token } })
  }
  catch (err) {
    errorMessage.value = err?.data?.message || err?.message || 'QR meja tidak valid atau sudah tidak aktif. Coba scan ulang.'
  }
  finally {
    isLoading.value = false
  }
}

onMounted(startSession)
</script>

<template>
  <div class="scan-screen d-flex align-center justify-center pa-4">
    <div class="scan-blob scan-blob--one" />
    <div class="scan-blob scan-blob--two" />

    <VCard class="scan-card pa-6 pa-sm-8 text-center" max-width="380" elevation="0">
      <template v-if="isLoading">
        <div class="scan-frame mb-6">
          <VIcon icon="ri-qr-code-line" size="56" color="primary" />
          <span class="scan-frame-line" />
          <span class="scan-corner scan-corner--tl" />
          <span class="scan-corner scan-corner--tr" />
          <span class="scan-corner scan-corner--bl" />
          <span class="scan-corner scan-corner--br" />
        </div>

        <h5 class="text-h5 font-weight-bold mb-1">
          Menghubungkan ke meja Anda
        </h5>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Mohon tunggu sebentar, kami sedang menyiapkan sesi pesanan Anda…
        </p>
      </template>

      <template v-else-if="errorMessage">
        <div class="scan-error-icon mb-5">
          <VIcon icon="ri-qr-scan-2-line" size="40" color="error" />
        </div>

        <h5 class="text-h5 font-weight-bold mb-2">
          Gagal Membuka Sesi
        </h5>
        <p class="text-body-2 text-medium-emphasis mb-6">
          {{ errorMessage }}
        </p>
        <VBtn block size="large" rounded="lg" @click="startSession">
          <VIcon icon="ri-refresh-line" start />
          Coba Lagi
        </VBtn>
      </template>
    </VCard>
  </div>
</template>

<style scoped>
.scan-screen {
  position: relative;
  min-block-size: 100vh;
  overflow: hidden;
  background: rgb(var(--v-theme-background));
}

.scan-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.35;
  z-index: 0;
}

.scan-blob--one {
  inline-size: 320px;
  block-size: 320px;
  inset-block-start: -120px;
  inset-inline-start: -100px;
  background: rgb(var(--v-theme-primary));
}

.scan-blob--two {
  inline-size: 260px;
  block-size: 260px;
  inset-block-end: -100px;
  inset-inline-end: -80px;
  background: rgb(var(--v-theme-secondary));
}

.scan-card {
  position: relative;
  z-index: 1;
  border-radius: 24px;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 24px 48px -20px rgba(var(--v-theme-on-surface), 0.2);
}

.scan-frame {
  position: relative;
  inline-size: 128px;
  block-size: 128px;
  margin-inline: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  background: rgba(var(--v-theme-primary), 0.08);
}

.scan-frame-line {
  position: absolute;
  inset-inline: 12px;
  block-size: 2px;
  background: linear-gradient(90deg, transparent, rgb(var(--v-theme-primary)), transparent);
  animation: scan-sweep 1.8s ease-in-out infinite;
}

@keyframes scan-sweep {
  0% { inset-block-start: 14px; opacity: 0; }
  15% { opacity: 1; }
  85% { opacity: 1; }
  100% { inset-block-start: 110px; opacity: 0; }
}

.scan-corner {
  position: absolute;
  inline-size: 18px;
  block-size: 18px;
  border-color: rgb(var(--v-theme-primary));
}

.scan-corner--tl { inset-block-start: 6px; inset-inline-start: 6px; border-block-start: 3px solid; border-inline-start: 3px solid; border-radius: 6px 0 0 0; }
.scan-corner--tr { inset-block-start: 6px; inset-inline-end: 6px; border-block-start: 3px solid; border-inline-end: 3px solid; border-radius: 0 6px 0 0; }
.scan-corner--bl { inset-block-end: 6px; inset-inline-start: 6px; border-block-end: 3px solid; border-inline-start: 3px solid; border-radius: 0 0 0 6px; }
.scan-corner--br { inset-block-end: 6px; inset-inline-end: 6px; border-block-end: 3px solid; border-inline-end: 3px solid; border-radius: 0 0 6px 0; }

.scan-error-icon {
  inline-size: 72px;
  block-size: 72px;
  margin-inline: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(var(--v-theme-error), 0.1);
}

@media (prefers-reduced-motion: reduce) {
  .scan-frame-line {
    animation: none;
  }
}
</style>
